import { Injectable, inject } from '@angular/core';

import { environment } from '@env/environment';

import { AuthToken } from '@personal-area/global/authorization/models';
import { AuthService } from '@personal-area/global/authorization/services';
import { Role, UserSettings, UserAccountTypes } from '@personal-area/global/users/models';

import { UserFacade } from '../state/facade/user';
import { UserApiService } from './user-api.service';

@Injectable({ providedIn: 'root' })
export class UserService {
  private _userFacade = inject(UserFacade);
  private _authService = inject(AuthService);
  private _userApiService = inject(UserApiService);

  public isEmailConfirmationRequired(): boolean {
    return (
      this._authService.isLoggedIn &&
      !this._userFacade.loggedUser.isEmailConfirmed &&
      !this._authService.hasMobileApp &&
      new Date(this._userFacade.loggedUser.createdAt).valueOf() >
        environment.requiredEmailConfirmationDate.valueOf()
    );
  }

  public addRoleToAccountTypes(role: Role): void {
    const token = this._authService.authToken;

    const usedAccountTypes = [...(token?.usedAccountTypes || []), role];

    this._userFacade.updateUserOptions({
      isTradingPlatform: usedAccountTypes.includes(Role.TradingPlatform),
      isPartner: usedAccountTypes.includes(Role.Partner),
      isExchanger: usedAccountTypes.includes(Role.Exchanger),
      isCabinetRole:
        usedAccountTypes.includes(Role.Trader) ||
        usedAccountTypes.includes(Role.Partner),
      isInvestor: usedAccountTypes.includes(Role.Investor),
    });

    this._authService.authToken = {
      ...token,
      usedAccountTypes,
    };
  }

  public loadUser(): void {
    if (this._authService.isLoggedIn) {
      this._userFacade.getUser(this._userFacade.loggedUser.id);
    } else {
      this._authService.logout();
    }
  }

  public updateUserSettings(
    language: string,
    currentGoogleClientId: string,
  ): void {
    if (
      !this._authService.isLoggedIn ||
      !this._userFacade.loggedUser.isLoaded
    ) {
      return;
    }

    const settings = <UserSettings>{
      clientId: this._userFacade.loggedUser.clientId || currentGoogleClientId,
      language: language
        ? language.substring(0, 2)
        : this._userFacade.loggedUser.language,
    };
    if (
      this._userFacade.loggedUser.language !== settings.language ||
      !this._userFacade.loggedUser.clientId
    ) {
      this._userApiService
        .updateSettings(this._userFacade.loggedUser.id, settings)
        .subscribe(() =>
          this._userFacade.updateUser({ language: settings.language }),
        );
    }
  }

  public getPersonType(token: AuthToken | null): { role: Role } & UserAccountTypes {
    const user = this._userFacade.loggedUser;

    if (!token || user.role === Role.TradingPlatform) {
      return {
        role: user.role,
        isTradingPlatform: user.isTradingPlatform,
        isTrader: user.isTrader,
        isPartner: user.isPartner,
        isExchanger: user.isExchanger,
        isCabinetRole: user.isCabinetRole,
        isInvestor: user.isInvestor,
      };
    }

    return {
      role: token?.role || user.role,
      isTrader: token.usedAccountTypes.includes(Role.Trader),
      isPartner: token.usedAccountTypes.includes(Role.Partner),
      isExchanger: token.usedAccountTypes.includes(Role.Exchanger),
      isTradingPlatform: token.usedAccountTypes.includes(Role.TradingPlatform),
      isInvestor: token.usedAccountTypes.includes(Role.Investor),
      isCabinetRole:
        token.usedAccountTypes.includes(Role.Trader) ||
        token.usedAccountTypes.includes(Role.Partner),
    };
  }
}
